import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { InfoOutlined, WarningAmberRounded } from '@mui/icons-material';
import { useGlobalization } from '@Translations/useGlobalization';
import { LOW_COVERAGE_PCT } from '../componentCoverage';

interface Props {
	pct: number;
	assets: number;
}

// Neutral grey data-coverage pill for a component row — same icon, wording
// and tooltip as the Score Setup header pill. Callers gate it with
// hasCoverageToShow, so pct/assets are always real here.
export const ComponentCoveragePill = ({ pct, assets }: Props) => {
	const { t } = useGlobalization();
	const low = pct < LOW_COVERAGE_PCT;
	const miss = Math.round((100 - pct) * 10) / 10;
	const title = low
		? t(
				'Only {{cov}}% of assets have data for this component. The remaining {{miss}}% will use the score you assign to "--".',
				{ cov: pct, miss },
		  )
		: t(
				'{{cov}}% of assets have data for this component. The rest use the score you assign to "--".',
				{ cov: pct },
		  );

	return (
		<Tooltip arrow title={title}>
			<Box
				component="span"
				data-cy="component-coverage-pill"
				sx={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: 0.5,
					px: 1,
					py: '2px',
					borderRadius: '9999px',
					fontSize: 11,
					fontWeight: 700,
					color: '#3a4451',
					background: '#eef1f4',
					whiteSpace: 'nowrap',
					cursor: 'default',
				}}>
				{low ? (
					<WarningAmberRounded
						sx={{ fontSize: 14, color: '#e0872f' }}
					/>
				) : (
					<InfoOutlined sx={{ fontSize: 14, color: '#97a0ac' }} />
				)}
				{pct}% {t('coverage')} · {assets.toLocaleString()} {t('assets')}
			</Box>
		</Tooltip>
	);
};

export default ComponentCoveragePill;
